// src/hooks/useCachedData.js
import { useState, useEffect, useCallback, useRef } from 'react';
import cacheManager from '../services/CacheManager';
import { isPermissionDenied } from '../utils/firestoreErrors';

// How long to wait before each retry of a failed read, in milliseconds. A
// read made just after a workspace is chosen can be refused because the ID
// token does not carry the new workspace's claim yet; it is in place a second
// or two later. Four tries over ~45 seconds covers that and a short network
// blip, and then the hook stops rather than polling a read that keeps failing.
export const DEFAULT_RETRY_DELAYS = [1000, 3000, 10000, 30000];

// An empty list is never served from or written to the cache. Most of what
// this hook holds (templates, operators, teams, customers) is empty only in a
// brand-new workspace, and caching an empty answer kept a list blank for the
// whole TTL whenever the read that produced it was wrong. Reading again costs
// one query; a blank filter for five minutes cost a support call.
const isEmptyResult = (value) =>
  value == null || (Array.isArray(value) && value.length === 0);

/**
 * Custom hook for cached data management
 * Provides memory + localStorage caching with automatic refresh
 *
 * `subscriberId` is the workspace the read is for. Pass null while the read
 * cannot succeed yet (no workspace, or a token that does not carry it): the
 * hook then waits, and loads as soon as an id arrives.
 *
 * A failed read is retried in the background after each of `retryDelays`, and
 * is never cached. The rows already on screen stay there while it retries.
 */
const useCachedData = (key, fetchFunction, options = {}) => {
  const {
    ttl = 5 * 60 * 1000, // 5 minutes default
    subscriberId, // Must be passed explicitly, no localStorage fallback
    lazyLoad = false, // Don't fetch until explicitly called
    backgroundRefresh = true, // Refresh stale data in background
    retryDelays = DEFAULT_RETRY_DELAYS,
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(!lazyLoad);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isMountedRef = useRef(true);
  // Always the latest fetch function, so a retry or a refresh reads with the
  // current workspace rather than the one it was scheduled under.
  const fetchFunctionRef = useRef(fetchFunction);
  fetchFunctionRef.current = fetchFunction;
  const retryDelaysRef = useRef(retryDelays);
  retryDelaysRef.current = retryDelays;
  // Bumped whenever the workspace changes. A read that started under an
  // older generation lands nowhere: one workspace's rows must never be set
  // (or cached) while another is selected.
  const generationRef = useRef(0);
  const retryTimerRef = useRef(null);
  const inFlightRef = useRef(null);
  const fetchDataRef = useRef(null);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  /**
   * Fetch data from cache or remote source.
   *
   * `attempt` counts retries of a failed read; callers leave it at 0.
   */
  const fetchData = useCallback(async (forceRefresh = false, attempt = 0) => {
    if (!subscriberId) {
      setLoading(false);
      return undefined;
    }

    const generation = generationRef.current;
    const isCurrent = () => isMountedRef.current && generation === generationRef.current;

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = cacheManager.get(key, subscriberId, ttl);
      if (cached.fromCache && !isEmptyResult(cached.data)) {
        setData(cached.data);
        setError(null);
        setLastUpdated(Date.now());
        setLoading(false);

        // Background refresh if enabled and data is getting stale
        if (backgroundRefresh && cached.source === 'localStorage') {
          // Don't await - let it run in background
          fetchData(true).catch(() => {});
        }
        return cached.data;
      }
    }

    // One read at a time per workspace: opening a popover while a retry is
    // already in flight joins it rather than starting a second query.
    if (inFlightRef.current && inFlightRef.current.promise && inFlightRef.current.generation === generation) {
      return inFlightRef.current.promise;
    }

    // Cache miss or forced refresh - fetch from remote. A forced refresh runs
    // behind the rows already on screen; only a first load shows as loading.
    if (!forceRefresh) setLoading(true);

    // Registered before the read starts, and cleared by identity, so a fetch
    // function that throws synchronously cannot leave a dead entry behind.
    const token = {};
    inFlightRef.current = { generation, token, promise: null };

    const promise = (async () => {
      try {
        const freshData = await fetchFunctionRef.current();
        if (!isCurrent()) return freshData;

        clearRetry();
        setData(freshData);
        setError(null);
        setLastUpdated(Date.now());
        setLoading(false);

        if (isEmptyResult(freshData)) {
          // Do not let an older, fuller copy outlive an answer of "none".
          cacheManager.invalidate(key, subscriberId);
        } else {
          cacheManager.set(key, freshData, subscriberId);
        }
        return freshData;
      } catch (err) {
        if (!isCurrent()) return undefined;

        const delays = retryDelaysRef.current || [];
        const willRetry = attempt < delays.length;
        // A refusal that will be tried again is the claim still landing, not a
        // fault: it is not worth an error in the console of every sign-in.
        if (willRetry && isPermissionDenied(err)) {
          console.debug(`Reading ${key} was refused, trying again shortly:`, err?.message);
        } else {
          console.error(`Error fetching ${key}:`, err);
        }
        setError(err?.message || `Failed to load ${key}`);
        setLoading(false);

        if (willRetry) {
          clearRetry();
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            if (fetchDataRef.current) {
              fetchDataRef.current(true, attempt + 1).catch(() => {});
            }
          }, delays[attempt]);
        }
        throw err;
      } finally {
        if (inFlightRef.current && inFlightRef.current.token === token) {
          inFlightRef.current = null;
        }
      }
    })();
    if (inFlightRef.current && inFlightRef.current.token === token) {
      inFlightRef.current.promise = promise;
    }
    return promise;
  }, [key, subscriberId, ttl, backgroundRefresh, clearRetry]);

  fetchDataRef.current = fetchData;

  /**
   * Refresh data (force fetch from remote). Starts a fresh round of retries.
   */
  const refresh = useCallback(() => {
    clearRetry();
    return fetchData(true);
  }, [fetchData, clearRetry]);

  /**
   * Update cache with new data (optimistic updates)
   */
  const updateCache = useCallback((newData) => {
    if (!subscriberId) return;

    setData(newData);
    setLastUpdated(Date.now());
    if (isEmptyResult(newData)) {
      cacheManager.invalidate(key, subscriberId);
    } else {
      cacheManager.set(key, newData, subscriberId);
    }
  }, [key, subscriberId]);

  /**
   * Invalidate cache and refresh
   */
  const invalidateAndRefresh = useCallback(() => {
    if (!subscriberId) return undefined;

    cacheManager.invalidate(key, subscriberId);
    clearRetry();
    return fetchData(true);
  }, [key, subscriberId, fetchData, clearRetry]);

  /**
   * Add item to cache (for create operations)
   */
  const addToCache = useCallback((newItem) => {
    if (!subscriberId) return;

    const updatedData = [...data, newItem];
    updateCache(updatedData);
  }, [data, updateCache, subscriberId]);

  /**
   * Update item in cache (for edit operations)
   */
  const updateInCache = useCallback((itemId, updatedItem) => {
    if (!subscriberId) return;

    const updatedData = data.map(item =>
      item.id === itemId ? { ...item, ...updatedItem } : item
    );
    updateCache(updatedData);
  }, [data, updateCache, subscriberId]);

  /**
   * Remove item from cache (for delete operations)
   */
  const removeFromCache = useCallback((itemId) => {
    if (!subscriberId) return;

    const updatedData = data.filter(item => item.id !== itemId);
    updateCache(updatedData);
  }, [data, updateCache, subscriberId]);

  /**
   * Mount / unmount. Set to true again on mount so StrictMode's
   * mount-unmount-mount in development does not leave the hook thinking it
   * is gone.
   */
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearRetry();
    };
  }, [clearRetry]);

  /**
   * Load on mount and whenever the workspace changes. One effect for both,
   * so a mount with a workspace already set reads once, not twice.
   */
  const previousSubscriberRef = useRef(undefined);
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    const previous = previousSubscriberRef.current;
    if (previous === subscriberId) return;
    previousSubscriberRef.current = subscriberId;

    generationRef.current += 1;
    inFlightRef.current = null;
    clearRetry();

    // Leaving a workspace: its rows must not stay on screen under another.
    if (previous) {
      setData([]);
      setLastUpdated(null);
      setError(null);
    }

    if (!subscriberId) {
      setLoading(false);
      return;
    }
    if (lazyLoad && !hasLoadedRef.current) return;

    hasLoadedRef.current = true;
    fetchData().catch(() => {});
    // fetchData is rebuilt with subscriberId, which is the dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriberId, lazyLoad]);

  return {
    data,
    loading,
    error,
    lastUpdated,

    // Actions
    refresh,
    fetchData: () => {
      hasLoadedRef.current = true;
      return fetchData(false);
    }, // For lazy loading
    invalidateAndRefresh,

    // Cache manipulation (for optimistic updates)
    updateCache,
    addToCache,
    updateInCache,
    removeFromCache,

    // Cache info
    isFromCache: lastUpdated !== null,
    cacheAge: lastUpdated ? Date.now() - lastUpdated : null
  };
};

export default useCachedData;
